import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Modal,
} from 'react-bootstrap';
import { useAchievementsContext } from '../../../contexts/AchievementsContext/AchievementsContext';
import { useCharacterExpressionsContext } from '../../../contexts/CharacterExpressionsContext/CharacterExpressionsContext';
import { RequestState } from '../../../dorian-shared/hooks/useAsyncOperationState';
import { useEpisodeNodes } from '../../../services/EpisodeTextService/hooks/useEpisodeNodes';
import { api } from '../../api';
import { ConfirmModal } from '../../ui/ConfirmModal/ConfirmModal';
import { TableEditor } from './components/TableEditor';
import { useSelectiveEditData } from './hooks/useSelectiveEditData';
import { useSelectiveEditValidation } from './hooks/useSelectiveEditValidation';
import css from './SelectiveEditModal.module.scss';
import { SelectiveEditModalProps } from './types/selectiveEditTypes';
import { buildEditContextPayload } from './utils/buildEditContextPayload';

export function SelectiveEditModal(props: SelectiveEditModalProps) {
  const {
    show,
    episodeId,
    bookId,
    preloadedNodes,
    memoryBank,
    endpointVersion,
    onHide,
    onSave,
    onNavigateToNode,
    isReadOnly = false,
    initialBranchId,
  } = props;

  const isV1Endpoint = endpointVersion === 'v1';

  const possibleExpressions = useCharacterExpressionsContext();
  const { achievements } = useAchievementsContext();

  const {
    nodes,
    episodeTextImport,
    validationCharacters,
    validationLocations,
    requestState,
    errorMessage,
    isParsing,
  } = useEpisodeNodes(episodeId, {
    enabled: show && !isV1Endpoint,
    preloadedNodes,
    bookId,
    expressions: possibleExpressions,
    memoryBank: memoryBank as { id: number; name: string }[],
    achievements,
  });

  // Derive validation config
  const characterNamesInBook = useMemo(
    () => validationCharacters.map((c) => c.alias),
    [validationCharacters],
  );

  const rewardIds = useMemo(() => {
    const map = new Map<string, number>();
    (achievements ?? []).forEach((a) => {
      (a.rewards ?? []).forEach((r) => {
        if (r.id != null && a.name) {
          map.set(a.name.toLocaleLowerCase().trim(), r.id);
        }
      });
    });
    return map;
  }, [achievements]);

  const {
    nodeGroups,
    updateRowField,
    revertNode,
    isNodeDirty,
    isCellDirty,
    dirtyNodeIds,
    navigationMap,
    runValidation,
  } = useSelectiveEditData(
    episodeTextImport,
    nodes,
    characterNamesInBook,
    validationLocations,
    memoryBank ?? [],
    rewardIds,
  );

  const {
    hasNodeErrors,
    totalErrorCount,
    runValidation: triggerValidation,
  } = useSelectiveEditValidation(nodeGroups, runValidation);

  const totalErrorCountRef = useRef(totalErrorCount);
  totalErrorCountRef.current = totalErrorCount;

  // Stable refs for handleSave to avoid stale closures (F1)
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const characterNamesRef = useRef(characterNamesInBook);
  characterNamesRef.current = characterNamesInBook;
  const validationLocationsRef = useRef(validationLocations);
  validationLocationsRef.current = validationLocations;
  const memoryBankRef = useRef(props.memoryBank);
  memoryBankRef.current = props.memoryBank;
  const episodeTextImportRef = useRef(episodeTextImport);
  episodeTextImportRef.current = episodeTextImport;
  const validationCharactersRef = useRef(validationCharacters);
  validationCharactersRef.current = validationCharacters;
  const possibleExpressionsRef = useRef(possibleExpressions);
  possibleExpressionsRef.current = possibleExpressions;
  const dirtyNodeIdsRef = useRef(dirtyNodeIds);
  dirtyNodeIdsRef.current = dirtyNodeIds;

  const hasUnsavedChanges = dirtyNodeIds.length > 0;
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [pendingNavigateNodeId, setPendingNavigateNodeId] = useState<number | null>(null);
  const [saveInfo, setSaveInfo] = useState<{ message: string; hadErrors: boolean } | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup save timeout on unmount (F18)
  useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);

  const [isSaving, setIsSaving] = useState(false);

  // Save: build edit-context payload and PATCH to backend
  const handleSave = useCallback(async () => {
    const currentImport = episodeTextImportRef.current;
    if (!currentImport) return;
    if (totalErrorCountRef.current > 0) return;

    // Build PATCH /edit-context payload: diff of dirty nodes vs original branches
    const payload = buildEditContextPayload(
      currentImport,
      nodesRef.current,
      dirtyNodeIdsRef.current,
      validationCharactersRef.current,
      possibleExpressionsRef.current,
      memoryBankRef.current ?? [],
    );

    if (payload.nodes.length === 0) {
      setSaveInfo({ message: 'No changes to save', hadErrors: false });
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => setSaveInfo(null), 5000);
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.patch(`v1/stories/${episodeId}/edit-context`, payload);

      const warnings = response.data?.warnings as Array<{ field: string; message: string }> | undefined;
      if (warnings && warnings.length > 0) {
        // Some fields were ignored by backend — stay open and warn the user
        const ignoredFields = [...new Set(warnings.map((w) => w.field))].join(', ');
        console.warn('[SelectiveEditModal] Backend ignored fields:', warnings);
        setSaveInfo({
          message: `Saved, but some changes were not applied. Unsupported fields: ${ignoredFields}`,
          hadErrors: true,
        });
      } else if (onSave) {
        // Full success → notify parent (refresh + close)
        onSave();
      } else {
        onHide();
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string; details?: Array<{ message: string }> } }; message?: string };
      const status = axiosErr.response?.status;
      const details = axiosErr.response?.data?.details;

      let errorMsg: string;
      if (!axiosErr.response) {
        errorMsg = 'Network error — check your internet connection and try again';
      } else if (status === 404) {
        errorMsg = 'Save is not available yet. Please contact support or try again later.';
      } else if (status === 403) {
        errorMsg = 'You do not have permission to save changes to this episode';
      } else if (status === 422) {
        errorMsg = details?.map((d) => d.message).join('; ') || 'Validation failed — check your changes and try again';
      } else if (status && status >= 500) {
        errorMsg = 'Server error — please try again later';
      } else {
        errorMsg = axiosErr.response?.data?.error || `Unexpected error (${status || 'unknown'})`;
      }

      console.error('[SelectiveEditModal] Save error:', { status, data: axiosErr.response?.data, err });
      setSaveInfo({ message: errorMsg, hadErrors: true });
    } finally {
      setIsSaving(false);
    }
  }, [episodeId, onHide, onSave]);

  // Navigation (R7: use ref for hasUnsavedChanges to avoid stale closure)
  const handleNavigateToNode = useCallback((nodeTitle: string) => {
    if (!onNavigateToNode) return;
    const branchId = navigationMap.get(nodeTitle.toLowerCase());
    if (branchId == null) {
      console.warn(`[SelectiveEditModal] No Branch match for node title="${nodeTitle}"`);
      return;
    }
    if (hasUnsavedChangesRef.current) {
      setPendingNavigateNodeId(branchId);
      setShowCloseConfirm(true);
      return;
    }
    onHide();
    onNavigateToNode(branchId);
  }, [navigationMap, onHide, onNavigateToNode]);

  const handleRequestClose = useCallback(() => {
    if (hasUnsavedChangesRef.current) {
      setShowCloseConfirm(true);
      return;
    }
    onHide();
  }, [onHide]);

  // Keyboard shortcut: Ctrl+Enter / Cmd+Enter → Save
  const canSave = !isReadOnly && hasUnsavedChanges && !isSaving && requestState !== RequestState.Loading && totalErrorCount === 0;
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!show) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSaveRef.current) {
        e.preventDefault();
        e.stopPropagation();
        handleSaveRef.current();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [show]);

  // Determine content state
  const isLoading = requestState === RequestState.Loading;
  // isTableLoading: true during fetch AND parse phase — skeleton shown in TableEditor
  const isTableLoading = isLoading || isParsing;
  const isReady = !isLoading && !isParsing && !errorMessage && episodeTextImport;
  const showExportError = !isLoading && !isParsing && !errorMessage && !episodeTextImport && requestState === RequestState.Success;

  return (
    <Modal
      show={show}
      onHide={handleRequestClose}
      size="xl"
      scrollable
      dialogClassName={css.modalDialog}
    >
      <Modal.Header>
        <Modal.Title>
          Partial Edit
          {isReadOnly && (
            <span style={{ fontSize: '0.65em', marginLeft: 12, color: '#6c757d', fontWeight: 400 }}>
              Read-only — Live episode
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isV1Endpoint && (
          <Alert variant="warning" className="mb-0">
            Text Edit requires V2 or V3 endpoint. Please switch your endpoint version in settings.
          </Alert>
        )}
        {!isV1Endpoint && !errorMessage && !showExportError && isTableLoading && (
          <TableEditor
            nodeGroups={[]}
            onUpdateRowField={() => {}}
            isCellDirty={() => false}
            hasNodeErrors={() => false}
            isNodeDirty={() => false}
            onRevertNode={() => {}}
            isLoading
            isReadOnly={isReadOnly}
          />
        )}
        {!isV1Endpoint && !isTableLoading && !!errorMessage && (
          <Alert variant="danger" className="mb-0">{errorMessage}</Alert>
        )}
        {!isV1Endpoint && showExportError && (
          <Alert variant="danger" className="mb-0">
            Failed to load episode data. Please try again.
          </Alert>
        )}
        {saveInfo && (
          <Alert variant={saveInfo.hadErrors ? 'warning' : 'success'} className="mb-3">
            {saveInfo.message}
          </Alert>
        )}
        {isReady && (
          <TableEditor
            nodeGroups={nodeGroups}
            onUpdateRowField={updateRowField}
            isCellDirty={isCellDirty}
            hasNodeErrors={hasNodeErrors}
            isNodeDirty={isNodeDirty}
            onRevertNode={revertNode}
            onNavigateToNode={onNavigateToNode ? handleNavigateToNode : undefined}
            onFilteredCountChange={setFilteredCount}
            isReadOnly={isReadOnly}
            initialNodeId={initialBranchId != null ? `bid:${initialBranchId}` : undefined}
          />
        )}
      </Modal.Body>
      <Modal.Footer className={css.modalFooter}>
        <div className={css.footerSummary}>
          {isReady && (
            <>
              {!isReadOnly && (
                <>
                  <span className={css.footerBadgeEdited}>
                    {dirtyNodeIds.length}
                    {' edited'}
                  </span>
                  <span className={css.footerBadgeErrors}>
                    {totalErrorCount}
                    {' '}
                    {totalErrorCount === 1 ? 'error' : 'errors'}
                  </span>
                </>
              )}
              <span className={css.footerBadgeFiltered}
              >
                {filteredCount !== null && filteredCount < nodeGroups.length
                  ? `${filteredCount} of ${nodeGroups.length}`
                  : `${nodeGroups.length}`}
                {' nodes'}
              </span>
            </>
          )}
        </div>
        <div className={css.footerButtons}>
          {/* Validate button hidden for now — not useful at this stage
          <Button
            variant="outline-primary"
            disabled={isLoading || !isReady}
            onClick={triggerValidation}
          >
            Validate
          </Button>
          */}
          {!isReadOnly && (
            <Button
              variant="primary"
              disabled={!canSave}
              onClick={handleSave}
              title={totalErrorCount > 0 ? `Fix ${totalErrorCount} error(s) before saving` : 'Ctrl+Enter'}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}
          <div className={css.footerButtonSpacer} />
          <Button variant="outline-secondary" onClick={handleRequestClose}>Close</Button>
        </div>
      </Modal.Footer>
      <ConfirmModal
        centered
        show={showCloseConfirm}
        isLoading={false}
        title="Unsaved changes"
        confirmButtonTitle={pendingNavigateNodeId != null ? 'Go without saving' : 'Close without saving'}
        cancelButtonTitle="Stay"
        onConfirm={() => {
          setShowCloseConfirm(false);
          if (pendingNavigateNodeId != null && onNavigateToNode) {
            const nodeId = pendingNavigateNodeId;
            setPendingNavigateNodeId(null);
            onHide();
            onNavigateToNode(nodeId);
            return;
          }
          setPendingNavigateNodeId(null);
          onHide();
        }}
        onCancel={() => {
          setPendingNavigateNodeId(null);
          setShowCloseConfirm(false);
        }}
        backdrop="static"
      >
        {pendingNavigateNodeId != null
          ? 'You have unsaved changes. Go to node without saving?'
          : 'You have unsaved changes. Close without saving?'}
      </ConfirmModal>
    </Modal>
  );
}
